<!-- Labs_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Labs</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-amber-50 border-b border-amber-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-amber-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-amber-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="labs.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'flask']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No lab results found</p>
    </div>

    <div v-else class="p-4">
      <!-- Search -->
      <div v-if="labs.length > 10" class="mb-3">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search labs..."
          class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
      </div>

      <!-- Labs table -->
      <div class="space-y-1">
        <div
          v-for="(lab, i) in filteredLabs"
          :key="i"
          class="text-xs py-1.5 border-b border-gray-50 last:border-0"
        >
          <div class="flex items-center gap-2">
            <span class="text-gray-600 font-medium w-32 flex-shrink-0 truncate">
              {{ lab.CODE_DISPLAY || lab.CODE || 'Unknown' }}
            </span>
            <span class="font-medium" :class="lab.ABNORMAL_FLAG ? 'text-red-600' : 'text-gray-800'">
              {{ lab.VALUE_STRING || lab.VALUE_NUMERIC || '—' }}
              <span v-if="lab.UNITS" class="text-gray-400 font-normal ml-0.5">{{ lab.UNITS }}</span>
            </span>
            <FontAwesomeIcon v-if="lab.ABNORMAL_FLAG" :icon="['fas', 'exclamation-circle']" class="text-[9px] text-red-400" />
            <span v-if="lab.SERVICE_DATE" class="text-[10px] text-gray-400 ml-auto">{{ formatDate(lab.SERVICE_DATE) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Labs_History',
  components: { FontAwesomeIcon },
  props: {
    observations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      searchQuery: '',
    };
  },
  computed: {
    labs() {
      return this.observations
        .filter(o => (o.CATEGORY || '').toLowerCase() === 'lab')
        .sort((a, b) => (b.SERVICE_DATE || '').localeCompare(a.SERVICE_DATE || ''));
    },
    filteredLabs() {
      if (!this.searchQuery) return this.labs;
      const q = this.searchQuery.toLowerCase();
      return this.labs.filter(lab =>
        (lab.CODE_DISPLAY || '').toLowerCase().includes(q) ||
        (lab.CODE || '').toLowerCase().includes(q)
      );
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
